import React, { useEffect, useRef, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send } from "lucide-react";
import { toast } from "sonner";

const THREAD_POLL_MS = 5000;
const ACTIVE_POLL_MS = 3000;

export default function Messages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const lastCountRef = useRef(0);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
    } catch {}
  }, []);

  const loadThread = useCallback(async (threadId) => {
    if (!threadId) return;
    try {
      const { data } = await api.get(`/messages/thread/${threadId}`);
      setMsgs((prev) => {
        // only auto-scroll if a NEW message arrived
        if (data.messages.length > lastCountRef.current) {
          setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
        }
        lastCountRef.current = data.messages.length;
        return data.messages;
      });
    } catch {}
  }, []);

  // Initial load + threads polling
  useEffect(() => {
    loadThreads();
    const id = setInterval(loadThreads, THREAD_POLL_MS);
    return () => clearInterval(id);
  }, [loadThreads]);

  // Active thread polling
  useEffect(() => {
    if (!active?.thread_id) return;
    loadThread(active.thread_id);
    const id = setInterval(() => loadThread(active.thread_id), ACTIVE_POLL_MS);
    return () => clearInterval(id);
  }, [active?.thread_id, loadThread]);

  const openThread = (t) => {
    lastCountRef.current = 0;
    setMsgs([]);
    setActive(t);
  };

  const send = async () => {
    if (!body.trim() || !active || sending) return;
    const recipient_id = active.participants?.find((p) => p !== user?.user_id) || active.other?.user_id;
    if (!recipient_id) { toast.error("No recipient"); return; }
    const draft = body;
    setBody("");
    setSending(true);
    // optimistic append
    const tempId = "tmp_" + Date.now();
    setMsgs((m) => [...m, {
      message_id: tempId, sender_id: user?.user_id, sender_name: user?.name,
      body: draft, created_at: new Date().toISOString(),
    }]);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    try {
      await api.post("/messages", {
        thread_id: active.thread_id,
        recipient_id, body: draft, property_id: active.property_id,
      });
      await loadThread(active.thread_id);
      loadThreads();
    } catch {
      toast.error("Send failed");
      setMsgs((m) => m.filter((x) => x.message_id !== tempId));
      setBody(draft);
    } finally { setSending(false); }
  };

  return (
    <DashboardLayout>
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Inbox</p>
          <h1 className="font-display text-3xl font-semibold mt-2">Messages</h1>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--adn-emerald)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--adn-emerald)] animate-pulse" /> Live
        </span>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-0 bg-white border border-[var(--adn-line)] min-h-[500px]">
        <div className="border-r border-[var(--adn-line)] max-h-[600px] overflow-y-auto">
          {threads.length === 0 && <p className="p-5 text-sm text-[var(--adn-text-soft)]">No conversations yet.</p>}
          {threads.map((t) => {
            const isActive = active?.thread_id === t.thread_id;
            const lastFromOther = t.last_message && t.last_message.sender_id !== user?.user_id;
            return (
              <button
                key={t.thread_id}
                data-testid={`thread-${t.thread_id}`}
                onClick={() => openThread(t)}
                className={`w-full text-left p-4 border-b border-[var(--adn-line)] hover:bg-slate-50 transition-colors ${isActive ? "bg-slate-50" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm truncate">{t.other?.name || "Conversation"}</div>
                  {lastFromOther && !isActive && <span className="h-2 w-2 rounded-full bg-[var(--adn-gold)] shrink-0" aria-label="new" />}
                </div>
                <div className="text-xs text-[var(--adn-text-muted)] truncate mt-0.5">{t.last_message?.body || "—"}</div>
                <div className="text-[10px] text-[var(--adn-text-muted)] mt-1">{fmtDate(t.last_at)}</div>
              </button>
            );
          })}
        </div>

        <div className="md:col-span-2 flex flex-col">
          {!active ? (
            <div className="flex-1 grid place-items-center text-sm text-[var(--adn-text-soft)] p-8 text-center">
              Select a conversation to start chatting.
              <br />New messages appear automatically.
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-[var(--adn-line)] font-display font-semibold flex items-center justify-between">
                <span>{active.other?.name}</span>
                <span className="text-[10px] uppercase tracking-[0.12em] text-[var(--adn-text-muted)]">Auto-refreshing</span>
              </div>
              <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[460px]">
                {msgs.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                        <div className="text-[11px] text-[var(--adn-text-muted)] mb-1">
                          {mine ? "You" : m.sender_name} · {fmtDate(m.created_at)}
                        </div>
                        <div className={`px-3.5 py-2 rounded-lg text-sm leading-relaxed ${mine ? "bg-[var(--adn-navy)] text-white" : "bg-slate-100 text-[var(--adn-text)]"}`}>
                          {m.body}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-[var(--adn-line)] flex gap-2">
                <input
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                  placeholder="Type a message…"
                  className="flex-1 border border-[var(--adn-line)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--adn-navy)]"
                  data-testid="msg-input"
                  disabled={sending}
                />
                <button
                  data-testid="msg-send"
                  onClick={send}
                  disabled={sending || !body.trim()}
                  className="btn-gold px-4 py-2 rounded text-sm inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <Send size={14} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
